import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import LinesEllipsis from 'react-lines-ellipsis';

import LockIconGlyph from '@atlaskit/icon/core/lock-locked';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useWorkspaceId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { HelpIcon } from '@trello/nachos/icons/help';
import { Popover, usePopover } from '@trello/nachos/popover';
import type { OrganizationViewTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { BoardVisibilityBadge } from './BoardVisibilityBadge';
import { PrivateBoardInfoConnected as PrivateBoardInfo } from './PrivateBoardInfo';

import styles from './PrivateBoardTileLarge.less';

export const PrivateBoardTileLarge: FunctionComponent = () => {
  const workspaceId = useWorkspaceId();
  const { popoverProps, triggerRef, toggle, hide } = usePopover({
    placement: 'bottom-end',
  });

  const intl = useIntl();

  const { value: isRedesignEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  if (isRedesignEnabled) {
    return (
      <>
        <Button
          className={styles.newBoardTile}
          appearance="subtle"
          onClick={toggle}
          ref={triggerRef}
          testId={getTestId<OrganizationViewTestIds>('private-board')}
        >
          <div className={styles.tileDetails}>
            <div className={styles.iconContainer}>
              <div
                className={styles.lockIconContainer}
                title={intl.formatMessage({
                  id: 'board perms.private.short summary',
                  defaultMessage:
                    'Only board members can see and edit this board.',
                  description: 'Private visibility icon tooltip',
                })}
              >
                <LockIconGlyph
                  label=""
                  color={token('color.icon', '#FFFFFF')}
                />
              </div>
              <div className={styles.helpIconContainer}>
                <QuestionCircleIcon
                  label=""
                  color={token('color.icon', '#FFFFFF')}
                />
              </div>
            </div>
            <div className={styles.tileNameContainer}>
              <span className={styles.tileName}>
                <FormattedMessage
                  id="templates.board_list_item_add.private-board"
                  defaultMessage="Private board"
                  description="Private board tile placeholder"
                />
              </span>
            </div>
          </div>
        </Button>
        <Popover {...popoverProps} size="small">
          <PrivateBoardInfo
            orgId={workspaceId!}
            onClickOpenPlanSelection={hide}
          />
        </Popover>
      </>
    );
  }

  return (
    <>
      <Button
        className={styles.boardTile}
        appearance="subtle"
        onClick={toggle}
        ref={triggerRef}
      >
        <div className={styles.boardDetails}>
          <div className={styles.boardName}>
            <LinesEllipsis
              maxLine={2}
              text={intl.formatMessage({
                id: 'templates.board_list_item_add.private-board',
                defaultMessage: 'Private board',
                description: 'Private board tile placeholder',
              })}
              data-testid={getTestId<OrganizationViewTestIds>('private-board')}
            />
          </div>
          <div className={styles.badges}>
            <div className={styles.visiblityContainer}>
              <BoardVisibilityBadge
                visibility="private"
                color={token('color.icon.accent.red', '#C9372C')}
              />
            </div>
            <div className={styles.helpContainer}>
              <HelpIcon size="small" />
            </div>
          </div>
        </div>
      </Button>
      <Popover {...popoverProps} size="small">
        <PrivateBoardInfo
          orgId={workspaceId!}
          onClickOpenPlanSelection={hide}
        />
      </Popover>
    </>
  );
};
